import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import type { AxiosResponseErrorModel } from 'shared/lib/api';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { AlertModal, SideModal, SideModalBody, SideModalFooter } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { form } from 'shared/lib/form';

import { AddEmail } from './AddEmail';
import { EmailsEditorErrors } from './EmailsEditorErrors';

interface EmailsEditorProps {
  emails: string[];
  isLoading: boolean;
  submit: (emails: string[]) => void;
  error: AxiosResponseErrorModel | null;
}

export function EmailsEditor({ emails, submit, isLoading, error }: EmailsEditorProps) {
  const methods = useForm({
    defaultValues: {
      emails: emails.map((x) => ({ value: x })),
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'emails',
  });

  const submitHandler = methods.handleSubmit(({ emails }) => {
    submit(emails.map(({ value }) => value));
  });

  return (
    <SideModal autoTestAttributesName="email-notifications-modal">
      <SideModalBody title="Email notifications">
        <div className="mb-4 text-sm">Add email addresses to start receiving notifications.</div>
        <FormProvider {...methods}>
          <AlertModal
            autoTestAttributesName="email-notifications-exit-modal"
            showCondition={form(methods)}
          />
          <EmailsEditorErrors error={error} />
          <form onSubmit={submitHandler}>
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="mb-2 flex gap-4 last:!mb-0"
              >
                <Input
                  isDisabled={isLoading}
                  className="flex-1"
                  title="Email"
                  name={`emails.${index}.value`}
                  attributes={{
                    autoComplete: 'off',
                  }}
                  {...getDataAutoTestAttributes([`input-integration-email-${index + 1}`])}
                />
                <div
                  data-testid={`TestId_delete_emails.${index}.value`}
                  className="mt-4 select-none"
                >
                  <MaterialIcon
                    className="cursor-pointer text-[16px] text-deep-blue-500"
                    onClick={() => remove(index)}
                  >
                    delete_outline
                  </MaterialIcon>
                </div>
              </div>
            ))}
          </form>
        </FormProvider>
        <AddEmail
          inputsCount={fields.length}
          onClick={() => append({ value: '' })}
          isLoading={isLoading}
        />
      </SideModalBody>
      <SideModalFooter>
        <Button
          loading={isLoading}
          onClick={submitHandler}
          className="btn-primary h-14 w-full shrink-0"
          {...getDataAutoTestAttributes(['button-save-email-notifications'])}
        >
          Save changes
        </Button>
      </SideModalFooter>
    </SideModal>
  );
}
